import React, { useEffect, useState } from 'react';
import { Spinner } from '../../../../common';
import PollForm from './PollForm.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { AppBarCMS } from '../../../../basic';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useGetPollQuery,
  useGetPollResponsesMappedQuery,
  useGetPollsQuery,
  useSavePollMutation,
} from '../../../../../redux/services/polls';
import { useToastMessages } from '../../../../../hooks/useToastMessages';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearDraft,
  saveDraft,
} from '../../../../../redux/slices/pollDraftSlice.jsx';
import { cloneDeep } from 'lodash';
import useUserHasRole from '../../../../../hooks/useUserHasRoles.jsx';
import { ROLE_POOLS_ADMIN } from '../../../../../utils/constants/roles.js';

const PollsEdit = ({ mode }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';

  useEffect(() => {
    document.title = isEdit
      ? t('Úprava ankety') + ' - FRI CMS'
      : t('Pridanie ankety') + ' - FRI CMS';
  }, []); // Runs on mount

  const { data: responseCollections, isLoading: resCollLoading } =
    useGetPollResponsesMappedQuery();

  const { data: pollDetail, isLoading: pollLoading } = useGetPollQuery(id, {
    skip: isCreate,
  });

  const [savePoll] = useSavePollMutation();
  const isPollsAdmin = useUserHasRole(ROLE_POOLS_ADMIN);

  const { refetch } = useGetPollsQuery();
  const dispatch = useDispatch();
  const pollDraft = useSelector((state) => state.pollDraft);

  // A draft is used only for the poll it was saved from and only once,
  // so it does not leak into another poll or into a later visit.
  const draftKey = isCreate ? 'create' : String(id);
  const [draftValues] = useState(() =>
    pollDraft?.key === draftKey ? cloneDeep(pollDraft.values) : null,
  );
  useEffect(() => {
    dispatch(clearDraft());
  }, [dispatch]);

  const { saveSuccess: showSaveSuccess, saveFailed } = useToastMessages();

  const defaultInitials = {
    name: null,
    active: false,
    show_results: false,
    questions: [
      {
        response_collection_id: responseCollections?.find(
          (item) => item.is_active,
        )?.value,
        name: null,
      },
    ],
    polls_users_type: pollDetail?.polls_users_type
      ? pollDetail.polls_users_type
      : isCreate
        ? [1]
        : [],
    ...pollDetail,
  };

  const initials = draftValues ?? defaultInitials;

  const saveDraftToStore = (data) => {
    dispatch(saveDraft({ key: draftKey, values: cloneDeep(data) }));
  };

  return (!isCreate && pollLoading) || resCollLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={isCreate ? t('Pridanie ankety') : t('Úprava ankety')}
        backUrl={routes.cmsPolls}
        backUrlTooltip={t('Ankety')}
        permissionsBasic={'polls'}
      />
      <div style={{ margin: '10px', marginTop: '-10px' }}>
        <PollForm
          onSubmit={async (values) => {
            try {
              await savePoll({
                body: values,
                id: isCreate ? undefined : id,
              }).unwrap();
              dispatch(clearDraft());
              showSaveSuccess();
              navigate(routes.cmsPolls);
              refetch();
            } catch (err) {
              saveFailed(err);
            }
          }}
          initialValues={initials}
          pollDetail={pollDetail}
          alreadyVoted={
            pollDetail !== undefined && pollDetail.noOfResponses > 0
          }
          canEditAnswered={isPollsAdmin}
          responseCollections={responseCollections}
          saveDraft={saveDraftToStore}
        />
      </div>
    </>
  );
};

export default PollsEdit;
